/** Legal page content for /terms, /code-of-conduct, /privacy.
 * Adapted from owner-supplied CodePen-style structure; entity facts limited to
 * what exists in this project (no invented corporate street address).
 */

export type LegalClause = {
  official: string;
  plain: string;
};

export type LegalSection = {
  heading: string;
  body: string[];
};

export const LEGAL_EFFECTIVE_DATE = "September 28, 2026";

export const TERMS_INTRO_PLAIN =
  "By creating an account or using TNK Main, you agree to everything on this page. These rules protect you and us so we can operate the service. If you break them, we may suspend or end your access.";

export const TERMS_CLAUSES: LegalClause[] = [
  {
    official:
      'These Terms of Service (the "Terms of Service") are a contract between you, the user, and the operator of TNK Main / AI For POS (the "Company"), which operates the website at https://tnkmain.tnktrade.ai (the "TNK Main Site") and related services reachable from that site. By using the TNK Main Site and any services accessible from it — including customer accounts, AI model training and prediction, POS and cart trials, website server ordering, document/RAG features, and related product pages (collectively, the "Service" or "Services") — you agree to be bound by these Terms of Service. If you do not agree to these Terms of Service or any part thereof, your only remedy is not to use the TNK Main Site or the Services. VIOLATION OF ANY OF THE TERMS OF SERVICE BELOW MAY RESULT IN TERMINATION OF YOUR RIGHT TO USE THE SERVICE AND ANY ACCOUNT YOU CREATED. YOU AGREE TO USE THE SERVICE AT YOUR OWN RISK. The Company reserves the right to refuse service to anyone for any reason at any time.',
    plain: TERMS_INTRO_PLAIN,
  },
  {
    official:
      "You must provide accurate registration information and keep your login credentials confidential. You are responsible for all activity under your account. Notify us promptly if you believe your account was compromised. Password-reset and OAuth login features (Google, LinkedIn, GitHub) are provided subject to these Terms and the Privacy Policy.",
    plain:
      "Keep your email and password safe. You are responsible for what happens on your account. Use Forgot password or OAuth only for your own account.",
  },
  {
    official:
      "You may not use the Services for unlawful purposes; to infringe intellectual property or privacy rights; to upload malware or attempt unauthorized access; to harass others; to spam; or to disrupt the Services. Customer content you upload (for example training data, documents, or prompts) remains your responsibility. You represent that you have the rights needed to use that content with the Services. Site software and branding remain copyright © 2024–2026 TNK Main / AI For POS unless otherwise stated.",
    plain:
      "Don't break the law, harass people, hack us, or upload junk you don't have rights to. Your data is your responsibility. Our software copyright stays with us.",
  },
  {
    official:
      "Paid products and trials (including PayPal checkout for model access, server deposits, and related orders) are described on the applicable product pages. Refund and trial windows stated on those pages apply. Fees processed through PayPal are subject to PayPal's terms. The Company does not store full payment card numbers on the TNK Main Site when checkout is completed via PayPal.",
    plain:
      "Prices, trials, and refunds are what the product page says. Payments go through PayPal. We don't keep your full card number on this site when you pay with PayPal.",
  },
  {
    official:
      'THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, TO THE MAXIMUM EXTENT PERMITTED BY LAW. THE COMPANY IS NOT LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR LOSS OF DATA, PROFITS, OR BUSINESS, ARISING FROM YOUR USE OF THE SERVICES.',
    plain:
      "We work hard to keep things running, but we can't promise perfection. We're not on the hook for side effects or lost profits if something goes wrong, as far as the law allows.",
  },
  {
    official:
      "The Company may update these Terms of Service by posting a revised version on the TNK Main Site. Continued use after changes become effective constitutes acceptance. Material changes may also be noted via site notice or account email when practical.",
    plain:
      "We can update these terms on the site. If you keep using TNK Main after that, you're agreeing to the new version.",
  },
];

export const CONDUCT_SECTIONS: LegalSection[] = [
  {
    heading: "Welcome",
    body: [
      "TNK Main is a product and customer community space. Everyone is welcome to participate regardless of race, religion, gender, sexual orientation, appearance, or anything else that makes up a person. We want everyone to feel comfortable and safe.",
      "These rules of conduct apply to public and account-facing actions on TNK Main.",
    ],
  },
  {
    heading: "Where these rules apply",
    body: [
      "Creating or updating a customer account profile",
      "Content you upload (training files, documents, prompts, comments if enabled)",
      "Messages or support requests you send through the site",
      "Any other place you create something or communicate through TNK Main",
    ],
  },
  {
    heading: "Unacceptable",
    body: [
      "Pornographic / sexual content",
      "Bigoted, sexist, or racist content",
      "Rude attacks, intimidation, harassment, or stalking",
      "Disruptive or offensive abuse of the Services",
      "Spam",
      "Taking credit for other people's work, posting illegal content, or violating copyright — also covered by the Terms of Service",
      "This is not a complete list. Don't be a jerk.",
    ],
  },
  {
    heading: "Who decides",
    body: [
      "We do — the TNK Main / AI For POS team. Calls can be subjective. We err on the side of keeping users safe and comfortable, and on the side of someone reporting they were made uncomfortable. Ultimately it is our call.",
      "You don't get to decide how other people feel about your actions; they do.",
    ],
  },
  {
    heading: "What happens",
    body: [
      "Harmful content may be deleted.",
      "If you are notified of unacceptable behavior, stop immediately.",
      "We understand people make mistakes. We will not ban you for a slight misstep. Repeated or egregious violations can result in account deletion and a ban.",
    ],
  },
  {
    heading: "How to report",
    body: [
      "Use the site contact footer (#contact) or email ops via your usual TNKTrade contact channel and describe what happened. We will handle it as soon as we can.",
    ],
  },
  {
    heading: "Acceptable",
    body: [
      "Being a kind, supportive, helpful community member and customer.",
    ],
  },
];

export const PRIVACY_SECTIONS: LegalSection[] = [
  {
    heading: "Account information",
    body: [
      "If you register, we store account fields needed to operate the Service (for example email, name, password hash or OAuth identity link, language/currency preferences, and favorites).",
      "You do not have to use a display name beyond what the registration form requires. You can update profile fields from your account when logged in.",
      "OAuth logins (Google, LinkedIn, GitHub) supply identity and email from those providers when you authorize them. We do not receive your social-network password.",
    ],
  },
  {
    heading: "How we use email",
    body: [
      "We use your email for service messages such as registration confirmation, password-reset OTP/link messages, and operational notices about orders or account security.",
      "Product or ops alerts tied to purchases may use configured SES source addresses (for example no_reply@tnktrade.ai).",
    ],
  },
  {
    heading: "Payments",
    body: [
      "Checkout for paid products is processed through PayPal. We do not have direct access to your full payment card number when you complete payment on PayPal.",
      "We store order-related records needed to fulfill and support purchases (for example email, product, amount, status).",
    ],
  },
  {
    heading: "Product data you upload",
    body: [
      "If you use AI model training, prediction, RAG/document features, or similar tools, files and prompts you submit are processed to provide that feature.",
      "Treat uploaded business data as confidential on your side; do not upload data you are not allowed to process.",
    ],
  },
  {
    heading: "Accessing or correcting information",
    body: [
      "You can view and update profile fields while logged in to your account.",
      "For deletion or export requests, contact us through the site contact footer. We will process lawful requests as required.",
    ],
  },
  {
    heading: "Security",
    body: [
      "We use reasonable technical and organizational measures (including hashed passwords, session tokens, captcha on auth flows when enabled, and OTP expiry for password reset).",
      "You must protect your login credentials. NO GUARANTEE CAN BE MADE THAT INFORMATION WILL BE SECURE FROM ALL INTRUSIONS OR UNAUTHORIZED RELEASE.",
    ],
  },
  {
    heading: "Cookies and local storage",
    body: [
      "We use cookies and browser storage as needed to keep you logged in, remember preferences (for example language/currency/favorites), and operate the site.",
      "Blocking cookies may break login or preference features.",
      "Third-party links (including payment or OAuth providers) are governed by those parties' policies.",
    ],
  },
  {
    heading: "Third parties",
    body: [
      "We use processors required to run the Service, including PayPal (payments), Amazon SES (email when enabled), hCaptcha (bot checks when enabled), and OAuth providers you choose (Google, LinkedIn, GitHub).",
      "We may disclose data if required by law, to enforce these policies, to protect rights and safety, or if the Company is acquired (with notice when practical).",
      "We do not sell your personal information to third parties for their marketing.",
    ],
  },
  {
    heading: "Children",
    body: [
      "The Services are for individuals who can form binding contracts under applicable law. We do not knowingly collect personal information from children under 13 (or under 16 in the EU/EEA). If we learn we collected such information, we will delete it.",
    ],
  },
  {
    heading: "Changes",
    body: [
      `We may update this Privacy Policy. Significant changes will be posted on the TNK Main Site. Effective date: ${LEGAL_EFFECTIVE_DATE}.`,
    ],
  },
];
